import { Promise } from 'es6-promise';
import { IDropdownOption } from 'office-ui-fabric-react';
import { PropertyHelper, IOutput, IOutputValue, IComponent, IControlBlock, IAction, IOutputGroup, ISharedJourney, getTypes, getTemplate, IComponentDictionary, ILoop, EOutputScope, ActionHelper, EModalType, EComponentTypes } from './';
import store from '../store/store';
import types from '../store/types';

declare var jQuery;

/**
 * Outputs helper to get all Output Types templates and extract Outputs from Components.
 * @export
 * @class OutputsHelper
 */
export class OutputsHelper {

    /**
     * Get and store all Output templates.
     * @static
     * @returns {Promise<IOutput[]>} A promise containing a list of Output templates.
     * @memberOf OutputsHelper
     */
    public static GetTemplates(): Promise<IOutput[]> {

        let promise: Promise<IOutput[]> = new Promise((resolve, reject) => {

            getTypes()
                .then(types => {

                    let requests: Promise<IOutput>[] = types.map(getTemplate);

                    Promise.all(requests)
                        .then(templates => {

                            this.StoreTemplates(templates);
                            resolve(templates);

                        })
                        .catch(reject);

                })
                .catch(reject);

        });

        return promise;

    }

    /**
     * Store a list of Output template into the cache state.
     * @private
     * @static
     * @param {IOutput[]} templates List of Output templates.
     * @memberOf OutputsHelper
     */
    public static StoreTemplates(templates: IOutput[]): void {
        store.dispatch({ type: types.SET_OUTPUT_TYPES, templates: templates });
    }

    public static combineOutputs = (outputs: IOutputValue[], globalOutputs: IOutput[], parentLoopId: string, showLoopOutputs: boolean = false): IOutputValue[] => {

        if (!showLoopOutputs) {
            outputs = outputs.filter(o => o.OutputScope !== EOutputScope.LoopLocal || o.OutputScope === EOutputScope.LoopLocal && parentLoopId === o.LoopId);
        }
        // Exclude "orphan" outputs, i.e. outputs that have a non-existent parent component ID.
        let combined: IOutputValue[] = outputs
            .filter(o => !OutputsHelper.isOrphan(o))
            .concat(globalOutputs
                .map(o => {
                    return { ...o, componentId: null, OutputScope: EOutputScope.Global };
                }));

        return combined;

    };

    public static isOrphan = (output: IOutputValue): boolean => {
        let components = store.getState().processState.components;
        return !components[output.componentId];
    }

    public static removeRepeatableBlockOutputs = (outputs: IOutputValue[]): IOutputValue[] => {
        let components: IComponentDictionary = store.getState().processState.components;

        return outputs.filter(output => {
            return !(output.componentId && components[output.componentId] && components[output.componentId].ControlBlock &&
                !!(PropertyHelper.getPropertyValue(components[output.componentId].ControlBlock.Properties, 'Is Repeatable')));
        });
    }
    public static getOutputDropdownOptions = (outputs: IOutputValue[], previousOutputBlocks, currentComponentId?: string): IDropdownOption[] => {
        // Previous Output Blocks only contains outputs which are present in the UI.
        // Filtering using the previousOutputBlocks prevents outputs defined in other branches from being displayed

        let nonRepeatableOutputs = OutputsHelper.removeRepeatableBlockOutputs(outputs);
        let allOutputDropdownOptions: IDropdownOption[] = null;
        if (currentComponentId) {
            allOutputDropdownOptions = nonRepeatableOutputs
                .filter((output) => {
                    return output.Id && output.Name && output.componentId !== currentComponentId && !output.IsHidden;
                })
                .map((output, index) => {
                    let dropdownOption: IDropdownOption = {
                        index: index + 1,
                        key: output.Id,
                        text: output.Name
                    };
                    return dropdownOption;
                });
        }
        else {
            allOutputDropdownOptions = nonRepeatableOutputs
                //.filter(output => !output.componentId || previousOutputBlocks[output.componentId]) //TODO: fix previousOutputBlocks, which are currently limited by the displayed conditions
                .filter((output) => {
                    return output.Id && output.Name && !output.IsHidden;
                })
                .map((output, index) => {
                    let dropdownOption: IDropdownOption = {
                        index: index + 1,
                        key: output.Id,
                        text: output.Name
                    };
                    return dropdownOption;
                });
        }


        let uniqueOutputIds: string[] = [];
        let uniqueOutputDropdownOptions: IDropdownOption[] = [];

        jQuery.each(allOutputDropdownOptions, function (i, e) {
            if (jQuery.inArray(e.key, uniqueOutputIds) === -1) {
                uniqueOutputIds.push(e.key);
                uniqueOutputDropdownOptions.push(e);
            }
        });

        return uniqueOutputDropdownOptions;
    }

    public static getFilteredOutputDropdownOptions = (outputs: IOutputValue[], previousOutputBlocks, outputSearchTerm: string, currentComponentId?: string): IDropdownOption[] => {

        return OutputsHelper.getOutputDropdownOptions(outputs, previousOutputBlocks, currentComponentId).filter(
            (o) => {
                return o.text.toLowerCase().indexOf(outputSearchTerm.toLowerCase().replace(/\s+/g, " ")) >= 0;
            }
        );
    }
    public static getGlobalOutputDropdownOptions = (outputs: IOutputValue[]) => {

        // Filter out all outputs that relate to a component, thus leaving only the global outputs

        return outputs
            .filter(output => !output.componentId)
            .map((output, index) => {
                let dropdownOption: IDropdownOption = {
                    index: index + 1,
                    key: output.Id,
                    text: output.Name
                };
                return dropdownOption;
            });

    }

    public static getOutputTypeDropdownOptions = (outputTypes: IOutput[]): IDropdownOption[] => {

        return outputTypes.map((o) => {
            return {
                index: o.Type + 1,
                key: o.Type,
                text: o.Name
            };
        });

    }

    public static getOutputValueDropdownOptions = (action: IAction): IDropdownOption[] => {

        if (!action.OutputValueOptions)
            return null;

        return action.OutputValueOptions
            .filter(filteredOutputValueOption => filteredOutputValueOption.FilteringPropertyValue === PropertyHelper.getPropertyValue(action.Properties, filteredOutputValueOption.FilteringPropertyName))
            .map((filteredOutputValueOption, index) => {
                return {
                    index: index + 1,
                    key: filteredOutputValueOption.Name,
                    text: filteredOutputValueOption.Name
                };
            });
    }

    /**
     * Combine a set of Component Outputs and Global Outputs into a single set of IOutputValue.
     * @static
     * @param {IOutputValue[]} outputs Component Outputs.
     * @param {IOutput[]} globalOutputs Global Outputs.
     * @returns {IOutputValue[]} List of combined Component Outputs and Global Outputs as OutputValue.
     * @memberOf OutputsHelper
     */
    public static CombineOutputs(outputs: IOutputValue[], globalOutputs: IOutput[]): IOutputValue[] {

        let globalOutputValues: IOutputValue[] = globalOutputs
            .map(g => {
                return { ...g, componentId: null, OutputScope: EOutputScope.Global };
            });
        let combinedOutputs: IOutputValue[] = [...globalOutputValues, ...outputs];

        return combinedOutputs;

    }

    /**
     * Get all Outputs from a dictionnary of components.
     * @static
     * @param {{[id: string]: IComponent}} components Component dictionnary.
     * @returns {IOutputValue[]} List of all Component Outputs.
     * @memberOf OutputsHelper
     */
    public static GetOutputsFromComponents(components: { [id: string]: IComponent }): IOutputValue[] {

        let outputs: IOutputValue[] = [];

        for (let id in components) {

            let component: IComponent = components[id];
            let { ControlBlock, Action, Loop } = component;

            if (ControlBlock)
                outputs = outputs.concat(this.GetOutputsFromControlBlock(ControlBlock, component.ParentLoopId));
            else if (Action)
                outputs = outputs.concat(this.GetOutputsFromAction(Action, component.ParentLoopId));
            else if (Loop)
                outputs = outputs.concat(this.GetOutputsFromLoop(Loop));

        }

        return outputs;
    }

    /**
     * Get all Outputs from a Control Block.
     * @private
     * @static
     * @param {IControlBlock} controlBlock Control Block to get the list of Outputs from.
     * @returns {IOutputValue[]} List of all Outputs within the Control Block.
     * @memberOf OutputsHelper
     */
    private static GetOutputsFromControlBlock(controlBlock: IControlBlock, parentLoopId: string): IOutputValue[] {

        let outputs: IOutputValue[] = [];

        controlBlock.Controls
            .forEach(c => outputs = outputs
                .concat((c.OutputValues || [])
                    .map(o => {
                        return {
                            ...o,
                            componentId: controlBlock.ComponentId,
                            LoopId: parentLoopId,
                            OutputScope: parentLoopId ? EOutputScope.LoopLocal : EOutputScope.Local
                        };
                    })));

        return outputs;
    }

    /**
     * Get all Outputs from a Action.
     * @private
     * @static
     * @param {IAction} action Action to get the list of Outputs from.
     * @returns {IOutputValue[]} List of all Outputs within the Action.
     * @memberOf OutputsHelper
     */
    private static GetOutputsFromAction(action: IAction, parentLoopId: string): IOutputValue[] {

        let outputs: IOutputValue[] = action.OutputValues
            .map(o => {
                return {
                    ...o,
                    componentId: action.ComponentId,
                    LoopId: parentLoopId,
                    OutputScope: parentLoopId ? EOutputScope.LoopLocal : EOutputScope.Local
                };
            });

        return outputs;
    }

    private static GetOutputsFromLoop(loop: ILoop): IOutputValue[] {

        let outputs: IOutputValue[] = loop.OutputValues
            .map(o => {
                return { ...o, componentId: loop.ComponentId };
            });

        return outputs;
    }

    /**
     * Save all Outputs in their respective states.
     * @static
     * @param {IOutputValue[]} outputs Outputs to be updated.
     * @memberOf OutputsHelper
     */
    public static SaveOutputs(outputs: IOutputValue[]): void {

        let globalOutputs: IOutputValue[] = outputs.filter(o => !o.componentId);
        let componentOutputs: IOutputValue[] = outputs.filter(o => o.componentId);

        this.SaveGlobalOutputs(globalOutputs);
        componentOutputs.forEach(this.SaveComponentOutput);

    }

    /**
     * Save an Output related to a journey component.
     * @private
     * @static
     * @param {IOutputValue} output Output to update.
     * @memberOf OutputsHelper
     */
    private static SaveComponentOutput(output: IOutputValue): void {

        let component: IComponent = store.getState().processState.components[output.componentId];
        let { ControlBlock, Action, SharedJourney, Loop } = component;

        if (ControlBlock)
            OutputsHelper.SaveControlOutput(output, ControlBlock);
        else if (Action)
            OutputsHelper.SaveActionOutput(output, Action);
        else if (SharedJourney)
            OutputsHelper.SaveSharedJourneyOutput(output, SharedJourney);
        else if (Loop)
            OutputsHelper.SaveLoopOutput(output, Loop);

    }

    /**
     * Save a set of global outputs.
     * @private
     * @static
     * @param {IOutputValue[]} outputs Global outputs to be updated.
     * @memberOf OutputsHelper
     */
    private static SaveGlobalOutputs(outputs: IOutputValue[]): void {
        store.dispatch({ type: types.UPDATE_GLOBAL_OUTPUTS, outputs: outputs });
    }

    /**
     * Save an Output related to a Control.
     * @private
     * @static
     * @param {IOutputValue} output Output to update.
     * @param {IControlBlock} controlBlock Control Block the Output relates to.
     * @memberOf OutputsHelper
     */
    private static SaveControlOutput(output: IOutputValue, controlBlock: IControlBlock): void {

        let { Id } = output;

        let controls = controlBlock.Controls
            .map((c, i) => {
                return { control: c, index: i };
            });

        controls = controls.filter((c) => {
            return c.control.OutputValues !== null && c.control.OutputValues.filter(o => o.Id === Id).length;
        });

        if (!controls.length)
            return;

        let { control, index } = controls[0];
        let outputs = control.OutputValues
            .map((o, i) => {
                return { output: o, index: i };
            })
            .filter(o => o.output.Id === Id);

        let controlOutput = { ...output };
        delete controlOutput.componentId;

        control.OutputValues[outputs[0].index] = controlOutput;

        store.dispatch({
            type: types.UPDATE_CONTROL,
            componentId: output.componentId,
            controlIndex: index,
            control: control
        });

    }

    /**
     * Save an Output related to an Action.
     * @private
     * @static
     * @param {IOutputValue} output Output to update.
     * @param {IAction} action Action the Output relates to.
     * @memberOf OutputsHelper
     */
    private static SaveActionOutput(output: IOutputValue, action: IAction): void {

        let matches = action.OutputValues
            .map((o, i) => {
                return { output: o, index: i };
            })
            .filter(o => o.output.Id === output.Id);

        if (!matches.length)
            return;

        let actionOutput = { ...output };
        delete actionOutput.componentId;

        action.OutputValues[matches[0].index] = actionOutput;

        store.dispatch({
            type: types.UPDATE_ACTION,
            componentId: output.componentId,
            action: action
        });

    }

    /**
     * @private
     * @static
     * @param {IOutputValue} output Output to update
     */
    private static SaveSharedJourneyOutput(output: IOutputValue, sharedJourney: ISharedJourney): void {

        let matches = sharedJourney.OutputValues
            .map((o, i) => {
                return { output: o, index: i };
            })
            .filter(o => o.output.Id === output.Id);

        if (!matches.length)
            return;

        let sharedJourneyOutput = { ...output };
        delete sharedJourneyOutput.componentId;

        sharedJourney.OutputValues[matches[0].index] = sharedJourneyOutput;

        store.dispatch({
            type: types.UPDATE_SHARED_JOURNEY,
            componentId: output.componentId,
            sharedJourney: sharedJourney
        });

    }

    private static SaveLoopOutput(output: IOutputValue, loop: ILoop): void {

        store.dispatch({
            type: types.UPDATE_LOOP_OUTPUT,
            componentId: output.componentId,
            output: output
        });

    }

    /**
     * Get a list of sorted Output Groups from a list of Outputs.
     * @static
     * @param {IOutputValue[]} outputs Outputs to extract the groups from.
     * @returns {IOutputGroup[]} Sorted list of Output Groups.
     * @memberOf OutputsHelper
     */
    public static GetGroups = (outputs: IOutputValue[]): IOutputGroup[] => {

        let groups: IOutputGroup[] = outputs
            .map(o => {
                o.Group = o.Group || '';
                return o;
            })
            // Get only group name
            .map(o => o.Group)
            // Remove duplicate group names
            .filter((g, i, a) => a.indexOf(g) === i)
            .map(g => {

                // Get and sort by Order all IDs of the outputs related to the group
                let groupOutputIds: string[] = outputs
                    .filter(o => o.Group === g)
                    .sort((o1, o2) => (o1.Order || 0) - (o2.Order || 0))
                    .map(o => o.Id);

                // Get the lowest Order property from related outputs
                let first: number = outputs
                    .filter(o => o.Id === groupOutputIds[0])[0].Order;

                return {
                    name: g,
                    outputs: groupOutputIds,
                    first: first
                };
            })
            // Sort groups by lowest Order property
            .sort((g1, g2) => g1.first - g2.first);

        return groups;

    };

    public static UpdateOutputOrders = (outputs: IOutputValue[], groups: IOutputGroup[]): IOutputValue[] => {

        let results: IOutputValue[] = [...outputs];
        let order: number = 0;

        groups.forEach(g => {
            g.outputs.forEach(id => {
                results.filter(r => r.Id === id)[0].Order = order++;
            });
        });

        return results;

    };

    /**
     * @static
     * @param outputs An array of outputs whose configuration will be checked for errors
     */
    public static getErrorMessages = (outputs: IOutput[]): string[] => {

        let requiredErrorMessages = outputs.filter(v => v.IsRequiredGlobalOutput).map((v) => {
            return v.GlobalOutputId ? '' : 'A Global Output must be specified for output ' + v.StaticName;
        }).filter(v => v.length);

        let logicalNameErrorMessages = outputs
            .filter((o) => {
                return !OutputsHelper.isValidOutputLogicalName(o.LogicalName);
            })
            .map((o) => {
                return 'The CRM Logical Name is invalid for output ' + o.Name;
            });
        ;

        return requiredErrorMessages.concat(logicalNameErrorMessages);

    }

    public static getErrorMessagesForGlobalAndLoopOutputs = (outputs: IOutput[], loopOutputs: boolean): string[] => {

        let errorMessages: string[] = [];
        let usedOutputNames = {};
        let usedOutputLogicalNames = {};

        outputs.forEach((o, i) => {

            if (!o.Name || !o.Name.length) {
                errorMessages.push('The output on row ' + (i + 1) + ' must be given a Name');
            }
            else {
                if (usedOutputNames[o.Name]) {
                    let errorMessage = 'The Name ' + o.Name + ' has been used for multiple outputs';
                    if (!errorMessages.some(e => e === errorMessage)) {
                        errorMessages.push(errorMessage);
                    }
                }
                else {
                    usedOutputNames[o.Name] = true;
                }
            }

            if (!loopOutputs) {

                if (!o.LogicalName || !o.LogicalName.length) {
                    errorMessages.push('The output on row ' + (i + 1) + ' must be given a Logical Name');
                }
                else {
                    if (usedOutputLogicalNames[o.LogicalName]) {
                        let errorMessage = 'The Logical Name ' + o.LogicalName + ' has been used for multiple outputs';
                        if (!errorMessages.some(e => e === errorMessage)) {
                            errorMessages.push(errorMessage);
                        }
                    }
                    else {
                        usedOutputLogicalNames[o.LogicalName] = true;
                    }
                }

            }

            if (/[^a-zA-Z0-9\-\\_\ /]/.test(o.Name)) {
                errorMessages.push('Only alphanumeric characters, spaces, hyphens, and underscores may be used for the Name on row ' + (i + 1));
            }

            if (/[^a-zA-Z0-9\-\\_\/]/.test(o.LogicalName)) {
                errorMessages.push('Only alphanumeric characters, hyphens, and underscores may be used for the Logical Name on row ' + (i + 1));
            }

            o.LogicalName = o.LogicalName ? o.LogicalName.toLowerCase() : '';
        });

        return errorMessages;

    }

    public static isValidOutputLogicalName = (outputLogicalName: string) => {
        return !outputLogicalName || !outputLogicalName.match(/[^a-z0-9_.]/);
    }

    public static GetOutput = (outputId: string): IOutput => {

        let outputsState = store.getState().outputsState;

        let output: IOutput = outputsState.globalOutputs.filter(o => o.Id === outputId)[0] || outputsState.outputs.filter(o => o.Id === outputId)[0];

        return output;

    }

    public static removeSpecifiedOutputOptions = (allOutputOptions: IDropdownOption[], outputOptionsToRemove: IOutput[]): IDropdownOption[] => {


        if (allOutputOptions && allOutputOptions.length && outputOptionsToRemove && outputOptionsToRemove.length) {

            return allOutputOptions.filter(o => {
                return !outputOptionsToRemove.some(v => v.Id === o.key.toString());
            });

        }

        return allOutputOptions;

    }

    // Return latest value for outputsInUse
    public static getLatestOutputsInUse(): {} {
        const extractKeyValuePairs = (input) => {
            const regex = /"([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})":|(##[^#]+##)/g;
            const dictionary = {};
            let match;
            var currentKey = null;
            while ((match = regex.exec(input)) !== null) {
                if (match[1] !== undefined) {
                    currentKey = match[1];
                    dictionary[currentKey] = [];
                } else if (match[2] !== undefined && currentKey !== null) {
                    dictionary[currentKey].push(match[2].slice(2, -2));
                }

            }
            return dictionary;
        };
        let jsonJourney = JSON.stringify(store.getState().processState.components);

        const componentOutput = extractKeyValuePairs(jsonJourney);

        // keep track of only the components where output values are used.
        let outputsInUse = {};
        Object.keys(componentOutput).forEach(x => {
            componentOutput[x].length ?
                outputsInUse[x] = componentOutput[x]
                :
                null
        });

        return outputsInUse;

    }

    public static syncOutputs(deletedOutputs: string[]) {

        store.dispatch({
            type: types.UPDATE_PROCESS_STATE,
            deletedOutputs: deletedOutputs,
        });

        // keep track of only the components where output values are used.
        let outputsInUse: {} = this.getLatestOutputsInUse();

        store.dispatch({
            type: types.UPDATE_OUTPUTS_IN_USE,
            outputsInUse: outputsInUse
        });

        // store.getState().modalState.modal.openModal = EModalType.None

    }

}


// WEBPACK FOOTER //
// ./source/ts/helpers/outputs.ts